// Card-effect CONFIGURATION: what each Fortress card does, expressed as pure
// serializable data that lives ON the card definition (`FortressCard.effects`)
// — and therefore inside every versioned `fortress_cards` catalogue document.
//
// Behavior is no longer keyed by card id. Each catalogue version carries its
// own effect configuration per card, so rebalances (token thresholds, bank
// targets, ranges) and even effect swaps ship as DATA in a new catalogue
// version; the engine only implements the finite vocabulary of effect KINDS
// below. A card with no `effects` is simply inert — implementable for its
// pattern but carrying no effect.
//
// Nothing effect-shaped is ever stored on game state. `FortressCardState`
// carries only the card id plus its mutable counters (tokens / stored /
// disabled / nodeIds); everything else — granted actions, passive rule
// changes, caps — is derived on demand from `player.fortressCards` × the
// match's card catalogue, on both the server (validation) and the client
// (availability UI).
//
// Win-predicate TIMING is the card's own `winTiming` field (also catalogue
// data); the `win.condition` here only says WHAT must hold. Token-threshold
// wins (`winAtTokens` / `winAtStored`) are checked at every immediate seam.
/** Vigor-zone level bounds ("energy level" in card text): 0 = forest … 4 = summit. */
export const ZONE_LOWEST = 0;
export const ZONE_HIGHEST = 4;
// CurdleCat's copy needs NOTHING here: the copy action AWARDS the player a
// separate phantom card instance (`FortressCardState.copied`) whose cardId is
// the copied card's — a plain instance the whole engine already understands
// (hooks, passives, grants, win predicates, resets, its own fresh
// tokens/stored state). Effect resolution stays a simple id lookup.
// --- Interpreters -----------------------------------------------------------
/** Sums a resource index across banks. */
const sumAt = (banks, i) => banks.reduce((s, b) => s + (b[i] ?? 0), 0);
/** Evaluate a declarative win condition against the pure query surface. */
export function winConditionMet(condition, q) {
    switch (condition.kind) {
        case "always":
            return true;
        case "tagged_count":
            return q.countTagged(condition.tag, condition.unique) >= condition.count;
        case "bank_at_least":
            return condition.amounts.every((want, i) => (q.bank[i] ?? 0) >= want);
        case "bank_any_at_least":
            return q.bank.some((have) => have >= condition.amount);
        case "marked_both_decks_implemented":
            // Latched by the hook only when a MARKED card is implemented, so an
            // unmarked card can never read as satisfied.
            return q.markedWinImplemented && q.markedRegularImplemented;
        case "production_and_bank_at_least":
            return ((q.production[condition.resource] ?? 0) >= condition.production &&
                (q.bank[condition.resource] ?? 0) >= condition.bank);
        case "bank_exceeds_all_others":
            return q.bank.some((have, i) => have > sumAt(q.othersBanks, i));
        case "sole_occupier_of_zone":
            return q.soleOccupierOfZone(condition.level);
        case "any_zone_fully_connected":
            return q.isAnyZoneFullyConnected(condition.acrossZones ?? false, condition.level);
        case "occupied_tiles_per_player":
            return ((condition.anywhere ? q.occupiedTilesAnywhere() : q.occupiedTilesConnectedToHome()) >=
                Math.ceil(condition.tiles / Math.max(1, q.playerCount)));
        case "fortress_fully_in_every_zone":
            return q.hasFortressFullyInEveryZone();
        case "scout_range_to_all_opponent_homes":
            return q.reachesAllOpponentHomesInScoutRange();
        case "card_nodes_all_in_zone":
            return q.cardNodesAllInZone(condition.level);
        case "mine_opposite_home":
            return q.hasOwnMineOppositeHome();
        case "opponent_fortresses_in_attack_range":
            return q.maxOpponentFortressesInAttackRange() >= condition.count;
        case "fortresses_in_attack_range":
            return q.hasBuildingInAttackRangeOfFortresses(condition.count, condition.minOpponent, condition.useDefendRange, condition.freeSourcesOnly);
    }
}
/** Evaluate a build gate: a why-not string for the UI, or null = buildable. */
export function gateReasonFor(gate, q) {
    if (!gate)
        return null;
    switch (gate.kind) {
        case "fortress_count_at_least":
            return q.fortressCount >= gate.count
                ? null
                : `Needs ${gate.count} or more fortresses under your control.`;
        case "mine_in_every_zone":
            return q.hasMineInEveryZone()
                ? null
                : "Needs one of your Mines in every zone.";
    }
}
// --- Derived helpers --------------------------------------------------------
/**
 * Does this card involve tokens at all — a printed cap, a token win threshold,
 * or any hook that places them? Card faces badge the token pool only for these;
 * on every other card a token never appears, so "holds ∞ tokens" would be a
 * lie of omission rather than information.
 */
export function usesTokens(fx) {
    if (!fx)
        return false;
    if (fx.tokenCap !== undefined || fx.winAtTokens !== undefined)
        return true;
    return (fx.hooks ?? []).some((h) => h.kind.endsWith("_token") || h.kind === "round_end_vigor_deposit");
}
/**
 * The card's decoy-copy configuration (Space Cheese Assassin), or undefined
 * when it makes no decoy. Both sides ask it: the server to recognise a decoy
 * in a pile (a card id whose implemented instance carries this hook), the
 * client to badge one.
 */
export function decoyCopyOf(fx) {
    return (fx?.hooks ?? []).find((h) => h.kind === "implement_decoy_copy");
}
/** Does the card's round-end energy deposit land on its STORED pile (Swole
 * Turtle "49"+), rather than on the token count as on earlier printings? Both
 * sides ask it: the server's collect pays out of the right pile, the client
 * gates and badges on it. */
export function depositsToStored(fx) {
    return (fx?.hooks ?? []).some((h) => h.kind === "round_end_vigor_deposit" && h.toStored === true);
}
/** The card's printed token cap, or +Infinity when it has none. */
export function tokenCapOf(fx) {
    return fx?.tokenCap ?? Number.POSITIVE_INFINITY;
}
/**
 * How many fortresses a player holds, as the rules count them: a CurdleCat
 * phantom (`copied`) is the SAME fortress as the CurdleCat that awarded it,
 * never a second one (Wei, 2026-10-17). Every "N fortresses" rule — Pipeline
 * Masterworks' gate, per-fortress income — counts through this.
 */
export function fortressCountOf(cards) {
    let n = 0;
    for (const c of cards)
        if (!c.copied)
            n += 1;
    return n;
}
/** THIS instance's token cap: the printed cap, doubled or halved (never
 * below 1) when a Big Boom rescaled it (`capScale` 1 / -1). Every cap check
 * against a held card goes through here; `tokenCapOf` alone is for the
 * catalogue card with no instance in hand. */
export function instanceTokenCap(fx, card) {
    const cap = tokenCapOf(fx);
    const scale = card?.capScale ?? 0;
    if (!Number.isFinite(cap) || scale === 0)
        return cap;
    return scale > 0 ? cap * 2 : Math.max(1, Math.floor(cap / 2));
}
/** Does implementing this card rescale another held fortress's token cap
 * (Big Boom's `scale_token_cap` implement choice)? */
export function implementScalesCap(fx) {
    const choice = fx?.implementChoice;
    return (choice !== undefined && choice !== "resource" && choice.kind === "scale_token_cap");
}
/** The number worth showing beside a card's token count: its win threshold
 * ("win at N tokens") when it has one, else its usage cap, else +Infinity. */
export function tokenTargetOf(fx) {
    return fx?.winAtTokens ?? fx?.tokenCap ?? Number.POSITIVE_INFINITY;
}
/** Does the effect bundle carry passive `kind`? */
export function hasPassive(fx, kind, choice) {
    return !!fx?.passives?.some((p) => p.kind === kind && passiveLiveFor(p, choice));
}
/** Is `passive` live on an instance whose recorded implement pick is
 * `choice`? Unconditional passives always are; an `ifChoice` passive only
 * when the holder denoted THAT piece on the card. */
export function passiveLiveFor(passive, choice) {
    return passive.ifChoice === undefined || passive.ifChoice === (choice ?? "");
}
/** What a capture costs the holder of these cards: the largest live
 * `capture_on_destroy` `amount` (default 1 — Telescopic Arm "14"-"53"; "54"+
 * prints 2), or 0 when none of them captures. */
export function capturePaymentFor(cards, effectOf) {
    let payment = 0;
    for (const fc of cards) {
        for (const p of effectOf(fc.cardId)?.passives ?? []) {
            if (p.kind !== "capture_on_destroy" || !passiveLiveFor(p, fc.choice))
                continue;
            payment = Math.max(payment, p.amount ?? 1);
        }
    }
    return payment;
}
/** The passives of `fx` live on an instance whose implement pick is
 * `choice` (see `passiveLiveFor`). */
export function livePassives(fx, choice) {
    return (fx?.passives ?? []).filter((p) => passiveLiveFor(p, choice));
}
/** The piece options a card's implement action must pick from, or null when
 * the card takes no piece pick (no choice, or a resource pick). */
export function implementPieceOptions(fx) {
    const choice = fx?.implementChoice;
    if (choice === undefined || choice === "resource")
        return null;
    if (choice.kind !== "piece")
        return null;
    return choice.options.length > 0 ? choice.options : null;
}
/** Does any implemented card of `cards` carry passive `kind` — live for that
 * instance's recorded implement pick? */
export function anyCardHasPassive(cards, kind, effectOf) {
    for (const fc of cards) {
        if (hasPassive(effectOf(fc.cardId), kind, fc.choice))
            return true;
    }
    return false;
}
/**
 * The turn-action allowance card effects grant `ownCards`'s holder, given
 * every seat's implemented cards (`allCards` includes the owner's): the max
 * `owner` allowance among the holder's cards, or the max `everyone` allowance
 * any card in play grants. 0 when no card grants one.
 */
export function grantedActionAllowance(ownCards, allCards, effectOf) {
    let best = 0;
    for (const fc of ownCards) {
        const a = effectOf(fc.cardId)?.actionAllowance;
        if (a)
            best = Math.max(best, a.owner);
    }
    for (const fc of allCards) {
        const a = effectOf(fc.cardId)?.actionAllowance;
        if (a)
            best = Math.max(best, a.everyone);
    }
    return best;
}
/**
 * The EXTRA action points `cards`'s holder takes every turn on top of their
 * allowance — the sum of every live `extra_actions` passive (catalogue "49"+:
 * Recyclomaniac, Cyclopz, Action Blaster each "+1 action per turn"). 0 when
 * none.
 */
export function extraActionsFor(cards, effectOf) {
    let extra = 0;
    for (const fc of cards) {
        for (const p of livePassives(effectOf(fc.cardId), fc.choice)) {
            if (p.kind === "extra_actions")
                extra += p.amount ?? 1;
        }
    }
    return extra;
}
/** The card's bank-or-production implement choice, or null when it takes
 * none (no choice, a resource pick, or a piece pick). */
export function implementBankOrProductionOf(fx) {
    const choice = fx?.implementChoice;
    if (choice === undefined || choice === "resource")
        return null;
    return choice.kind === "bank_or_production" ? choice : null;
}
/** The number worth showing beside a HELD card's token count — `tokenTargetOf`
 * with the instance's rescaled cap. */
export function instanceTokenTarget(fx, card) {
    return fx?.winAtTokens ?? instanceTokenCap(fx, card);
}
/**
 * Every special action the player's implemented cards grant right now. A
 * flipped (disabled) card grants nothing until it flips back. Derived — this
 * replaces the old stored `Player.actions` list.
 */
export function grantedActions(cards, effectOf) {
    const kinds = new Set();
    for (const fc of cards) {
        if (fc.disabled)
            continue;
        for (const kind of effectOf(fc.cardId)?.grants ?? [])
            kinds.add(kind);
    }
    return [...kinds];
}
/**
 * Can the player use `kind` via the specific card `cardId` right now? The one
 * gate both the server (dispatch validation) and the client (Use-button
 * availability) ask. Checks ownership, the grant itself, exhaustion, and the
 * token cap; handlers still enforce their own action-specific rules (targets,
 * costs, turn order).
 */
export function canUseCardAction(cards, cardId, kind, effectOf) {
    // Several instances can answer to one card id (BAM Exploder's endless
    // artifacts; a real card plus a CurdleCat-awarded copy of it): the action
    // is available if ANY of them can take it — the first failure reason
    // reports otherwise. Server dispatch resolves to the same instance via
    // `ownedCard`'s matching preference.
    const fx = effectOf(cardId);
    if (!fx?.grants?.includes(kind)) {
        let held = false;
        for (const fc of cards) {
            if (fc.cardId === cardId) {
                held = true;
                break;
            }
        }
        return {
            allowed: false,
            reason: held
                ? "That fortress doesn't grant this action."
                : "You don't hold that fortress.",
        };
    }
    // A grant the card's `uncappedGrants` lists spends no token, so a full
    // card still offers it; exhaustion (the flip) gates it like any other.
    const uncapped = fx.uncappedGrants?.includes(kind) === true;
    let firstFailure;
    let held = false;
    for (const fc of cards) {
        if (fc.cardId !== cardId)
            continue;
        held = true;
        if (fc.disabled) {
            firstFailure ??= { allowed: false, reason: "That fortress is exhausted." };
            continue;
        }
        if (!uncapped && fc.tokens >= instanceTokenCap(fx, fc)) {
            firstFailure ??= {
                allowed: false,
                reason: "No uses left — the card is at its token cap.",
            };
            continue;
        }
        return { allowed: true };
    }
    if (!held) {
        return { allowed: false, reason: "You don't hold that fortress." };
    }
    return firstFailure ?? { allowed: false, reason: "You can't use that effect." };
}
//# sourceMappingURL=registry.js.map